import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";

const LOGO_URL = "/images/logo-isiap.png";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/chi-siamo", label: "Chi Siamo" },
    { path: "/soluzioni", label: "Soluzioni" },
    { path: "/contatti", label: "Contatti" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top bar - Navy */}
      <div className="hidden md:block bg-[#1a1f36] text-white py-2">
        <div className="section-container flex justify-end items-center gap-6 text-sm">
          <a 
            href="mailto:info@isiap.it" 
            className="flex items-center gap-2 hover:text-[#FF6B00] transition-colors"
            data-testid="header-email"
          >
            <Mail size={14} />
            info@isiap.it
          </a>
          <a 
            href="tel:0383644311" 
            className="flex items-center gap-2 hover:text-[#FF6B00] transition-colors"
            data-testid="header-phone"
          >
            <Phone size={14} />
            0383 644311
          </a>
        </div>
      </div>

      {/* Main header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "glass shadow-sm border-b border-[#e2e4ed]"
            : "bg-white"
        }`}
        data-testid="main-header"
      >
        <div className="section-container">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center"
              data-testid="logo-link"
            >
              <img 
                src={LOGO_URL} 
                alt="iSiAP - Innovative Software Ideas" 
                className="h-10 md:h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    isActive(link.path)
                      ? "bg-[#FF6B00] text-white"
                      : "text-[#1a1f36] hover:bg-[#f5f6fa] hover:text-[#FF6B00]"
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden md:flex items-center gap-4">
              <Link
                to="/prenota"
                className="btn-primary text-sm"
                data-testid="header-cta"
              >
                Richiedi Demo
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-[#f5f6fa] transition-colors text-[#1a1f36]"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div 
            className="md:hidden absolute top-full left-0 right-0 bg-white border-b border-[#e2e4ed] shadow-lg animate-fade-in"
            data-testid="mobile-nav"
          >
            <div className="section-container py-4">
              <nav className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                      isActive(link.path)
                        ? "bg-[#FF6B00] text-white"
                        : "text-[#1a1f36] hover:bg-[#f5f6fa]"
                    }`}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(" ", "-")}`}
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
              <div className="mt-4 pt-4 border-t border-[#e2e4ed]">
                <Link
                  to="/prenota"
                  className="btn-primary w-full text-sm"
                  data-testid="mobile-cta"
                >
                  Richiedi Demo
                </Link>
              </div>
              <div className="mt-4 flex flex-col gap-2 text-sm text-[#4a5068]">
                <a href="mailto:info@isiap.it" className="flex items-center gap-2">
                  <Mail size={14} className="text-[#FF6B00]" /> info@isiap.it
                </a>
                <a href="tel:0383644311" className="flex items-center gap-2">
                  <Phone size={14} className="text-[#FF6B00]" /> 0383 644311
                </a>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
